import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Upload, Download, Trash2, FileText, Filter, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { downloadDocument, viewDocument } from '../../services/downloadService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: 'devis', label: 'Devis' },
  { value: 'facture', label: 'Factures' },
  { value: 'photos', label: 'Photos' },
  { value: 'courrier', label: 'Courrier' },
  { value: 'rapport', label: 'Rapport' },
  { value: 'piece_identite', label: 'Pièce d\'identité' },
  { value: 'contrat', label: 'Contrat' },
  { value: 'attestation', label: 'Attestation' },
  { value: 'autre_document', label: 'Autre document' },
  { value: 'kyc', label: 'KYC / Justificatifs' },
  { value: 'convention', label: 'Conventions' },
  { value: 'pv_reception', label: 'PV de Réception' },
  { value: 'administratif', label: 'Administratif' }
];

export default function GestionDocuments() {
  const { getAuthHeader } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Filtres
  const [filters, setFilters] = useState({
    document_type: 'all',
    reference_solidaire: '',
    date_debut: '',
    date_fin: ''
  });

  // Fichier à uploader
  const [uploadData, setUploadData] = useState({
    file: null,
    document_type: 'kyc',
    description: '',
    reference_solidaire: '',
    tags: ''
  });

  useEffect(() => {
    fetchDocuments();
  }, [filters]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.document_type) params.append('document_type', filters.document_type);
      if (filters.reference_solidaire) params.append('reference_solidaire', filters.reference_solidaire);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      params.append('page', '1');
      params.append('page_size', '100');

      const response = await axios.get(`${API}/ged/documents?${params}`, {
        headers: getAuthHeader()
      });
      // Support ancien format (array) et nouveau format (avec pagination) - Trier du plus récent au plus ancien
      const docs = response.data.data || response.data;
      const sortedDocs = Array.isArray(docs) ? docs.sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0)) : docs;
      setDocuments(sortedDocs);
    } catch {
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('document_type', uploadData.document_type);
      formData.append('description', uploadData.description);
      formData.append('reference_solidaire', uploadData.reference_solidaire);
      formData.append('tags', JSON.stringify(uploadData.tags.split(',').map(t => t.trim()).filter(Boolean)));

      await axios.post(`${API}/ged/documents/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Document uploadé avec succès');
      setUploadData({
        file: null,
        document_type: 'kyc',
        description: '',
        reference_solidaire: '',
        tags: ''
      });
      document.getElementById('file-input').value = '';
      fetchDocuments();
    } catch {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      await downloadDocument(doc);
      toast.success('Document téléchargé');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleView = async (doc) => {
    try {
      await viewDocument(doc);
    } catch {
      toast.error('Erreur lors de la visualisation');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/ged/documents/${documentId}`, {
        headers: getAuthHeader()
      });
      toast.success('Document supprimé');
      fetchDocuments();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Section Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Uploader un document
          </CardTitle>
          <CardDescription>Ajoutez un nouveau document à votre GED</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="file-input">Fichier *</Label>
              <Input
                id="file-input"
                type="file"
                onChange={handleFileChange}
                className="cursor-pointer"
              />
            </div>

            <div>
              <Label htmlFor="doc-type">Type de document *</Label>
              <Select
                value={uploadData.document_type}
                onValueChange={(value) => setUploadData({ ...uploadData, document_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="reference">Réf.Solidaire</Label>
              <Input
                id="reference"
                value={uploadData.reference_solidaire}
                onChange={(e) => setUploadData({ ...uploadData, reference_solidaire: e.target.value })}
                placeholder="Numéro de souscription..."
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                placeholder="Description du document..."
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="tags">Tags (séparés par des virgules)</Label>
              <Input
                id="tags"
                value={uploadData.tags}
                onChange={(e) => setUploadData({ ...uploadData, tags: e.target.value })}
                placeholder="important, urgent, convention..."
              />
            </div>

            <div className="md:col-span-2">
              <Button
                onClick={handleUpload}
                disabled={uploading || !uploadData.file}
                className="bg-coppam-gold hover:bg-coppam-gold/90"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Upload en cours...' : 'Uploader'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section Filtres */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Type de document</Label>
              <Select
                value={filters.document_type || "all"}
                onValueChange={(value) => setFilters({ ...filters, document_type: value === "all" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Réf.Solidaire</Label>
              <Input
                value={filters.reference_solidaire}
                onChange={(e) => setFilters({ ...filters, reference_solidaire: e.target.value })}
                placeholder="Rechercher..."
              />
            </div>

            <div>
              <Label>Date début</Label>
              <DatePickerFr
                className="w-full mt-1"
                value={filters.date_debut}
                onChange={(v) => setFilters({ ...filters, date_debut: v })}
              />
            </div>

            <div>
              <Label>Date fin</Label>
              <DatePickerFr
                className="w-full mt-1"
                value={filters.date_fin}
                onChange={(v) => setFilters({ ...filters, date_fin: v })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Mes Documents ({documents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-4 md:py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coppam-gold"></div>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-slate-600">
              Aucun document trouvé
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div key={doc.id} className="border rounded-lg p-4 hover:bg-slate-50 transition">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-5 w-5 text-coppam-blue" />
                        <h3 className="font-semibold">{doc.original_filename}</h3>
                        <Badge className="bg-slate-600 text-white font-semibold">
                          {DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type}
                        </Badge>
                      </div>
                      
                      {doc.description && (
                        <p className="text-sm text-slate-600 mb-2">{doc.description}</p>
                      )}
                      
                      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                        <span>📅 {formatDate(doc.uploaded_at)}</span>
                        <span>💾 {formatFileSize(doc.file_size)}</span>
                        {doc.reference_solidaire && (
                          <span>🔖 Réf: {doc.reference_solidaire}</span>
                        )}
                      </div>
                      
                      {doc.tags && doc.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {doc.tags.map((tag, idx) => (
                            <Badge key={idx} className="bg-purple-600 text-white text-xs font-semibold">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                        onClick={() => handleView(doc)}
                        title="Voir"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(doc)}
                        title="Télécharger"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(doc.id)}
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
